import type { Metric } from "@unbox/api";
import type React from "react";
import { useMemo } from "react";
import { getFilteredMetricsArray, METRICS_MAP } from "@/constants/metrics";
import { MetricsConfigurationModal, type MetricsFormData } from "../../../components/MetricsConfigurationModal";

export interface AnalyticsView {
  id: string;
  name: string;
  layout: "chart" | "table" | "bar";
  metrics: Metric[];
}

interface AnalyticsViewConfigurationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (view: AnalyticsView) => void;
  editMode?: boolean;
  existingView?: AnalyticsView;
}

// Custom name generator for Analytics Views (no date suffix)
const generateAnalyticsViewName = ({
  layout,
  chartMetrics,
  tableMetrics,
}: {
  layout: "chart" | "table" | "bar";
  chartMetrics: string[];
  tableMetrics: string[];
}) => {
  const validMetrics = layout === "chart" ? chartMetrics.filter((m) => m !== "") : tableMetrics.filter((m) => m !== "");

  if (validMetrics.length === 0) return "";

  // Convert metric IDs to labels using METRICS_MAP
  const metricLabels = validMetrics.map((m) => METRICS_MAP[m as Metric]?.label || m);

  if (metricLabels.length <= 2) {
    return metricLabels.join(", ");
  }
  return `${metricLabels.slice(0, 2).join(", ")}, ${metricLabels.length - 2} more`;
};

export const AnalyticsViewConfigurationModal: React.FC<AnalyticsViewConfigurationModalProps> = ({
  isOpen,
  onOpenChange,
  onSave,
  editMode = false,
  existingView,
}) => {
  // Check TikTok Ads connection status to filter Ads metrics
  const filteredMetrics = useMemo(() => getFilteredMetricsArray(), []);

  const handleSubmit = (data: MetricsFormData) => {
    const view: AnalyticsView = {
      id: existingView?.id || `view-${Date.now()}`,
      name: data.name,
      layout: data.layout,
      metrics: data.metrics as Metric[],
    };
    onSave(view);
  };

  return (
    <MetricsConfigurationModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      initialData={existingView}
      isEditMode={editMode}
      autoCloseOnSubmit={true}
      generateSuggestedName={generateAnalyticsViewName}
      filteredMetrics={filteredMetrics}
      copy={{
        createTitle: "Add Analytics View",
        editTitle: "Edit Analytics View",
        nameLabel: "View name",
        detailsSectionLabel: "Analytics View Details",
        detailsSectionSubtitle: "Name",
        submitLabels: {
          create: "Add View to Dashboard",
          edit: "Update View",
        },
      }}
    />
  );
};
